(() => {
  const ROOT_SELECTOR = "[data-ra-marquee]";
  const ROW_SELECTOR = "[data-ra-row]";
  const TRACK_SELECTOR = "[data-ra-track]";
  const STRIP_SELECTOR = "[data-ra-strip]";

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function rafThrottle(fn) {
    let ticking = false;
    return (...args) => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        fn(...args);
      });
    };
  }

  class RAMarquee {
    constructor(root) {
      this.root = root;
      this.rows = [];
      this.destroyed = false;
      this.lastTs = performance.now();

      this.rebuild();
      setTimeout(() => this.rebuild(), 150);

      this._onResize = rafThrottle(() => this.rebuild());
      window.addEventListener("resize", this._onResize, { passive: true });

      if ("ResizeObserver" in window) {
        this._ro = new ResizeObserver(this._onResize);
        this._ro.observe(root);
      }

      if (!prefersReducedMotion) this._tick();
    }

    rebuild() {
      if (this.destroyed) return;

      const rowEls = Array.from(this.root.querySelectorAll(ROW_SELECTOR));
      this.rows = rowEls.map((rowEl) => this._setupRow(rowEl)).filter(Boolean);
    }

    _setupRow(rowEl) {
      const track = rowEl.querySelector(TRACK_SELECTOR);
      const baseStrip = rowEl.querySelector(STRIP_SELECTOR);
      if (!track || !baseStrip) return null;

      const speed = parseFloat(rowEl.dataset.speed || "70"); // px/s
      const direction = (rowEl.dataset.direction || "left").toLowerCase();

      // Pause ONLY this row on hover
      rowEl.addEventListener("pointerenter", () => {
        rowEl.classList.add("is-paused");
      });
      rowEl.addEventListener("pointerleave", () => {
        rowEl.classList.remove("is-paused");
      });

      // Remove old clones
      Array.from(track.querySelectorAll("[data-ra-clone='1']")).forEach((n) => n.remove());

      // Ensure at least 2 strips
      const firstClone = baseStrip.cloneNode(true);
      firstClone.setAttribute("data-ra-clone", "1");
      firstClone.setAttribute("aria-hidden", "true");
      track.appendChild(firstClone);

      const stripWidth = Math.round(baseStrip.getBoundingClientRect().width);

      if (stripWidth < 5) {
        setTimeout(() => this.rebuild(), 120);
        return { rowEl, track, speed, direction, x: 0, stripWidth: 0 };
      }

      // Fill enough width so no empty space ever appears
      const needed = Math.max(this.root.clientWidth, rowEl.clientWidth) * 2;
      while (track.scrollWidth < needed) {
        const c = baseStrip.cloneNode(true);
        c.setAttribute("data-ra-clone", "1");
        c.setAttribute("aria-hidden", "true");
        track.appendChild(c);
        if (track.children.length > 30) break;
      }

      return {
        rowEl,
        track,
        speed,
        direction,
        // IMPORTANT: right-moving rows start offset → no left gap
        x: direction === "right" ? -stripWidth : 0,
        stripWidth
      };
    }

    _tick() {
      if (this.destroyed) return;

      const now = performance.now();
      const dt = Math.min(0.05, (now - this.lastTs) / 1000);
      this.lastTs = now;

      if (!prefersReducedMotion) {
        this.rows.forEach((r) => {
          if (!r || !r.stripWidth) return;

          // Pause ONLY this row
          if (r.rowEl.classList.contains("is-paused")) return;

          const dir = r.direction === "right" ? 1 : -1;
          r.x += dir * r.speed * dt;

          // Perfect wrap (no gap ever)
          if (dir === -1 && r.x <= -r.stripWidth) r.x += r.stripWidth;
          if (dir ===  1 && r.x >= 0)             r.x -= r.stripWidth;

          r.track.style.transform = `translate3d(${r.x}px,0,0)`;
        });
      }

      requestAnimationFrame(() => this._tick());
    }

    destroy() {
      this.destroyed = true;
      window.removeEventListener("resize", this._onResize);
      if (this._ro) this._ro.disconnect();
      this.rows = [];
    }
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.__raMarquee) return;
      root.__raMarquee = new RAMarquee(root);
    });
  }

  document.addEventListener("DOMContentLoaded", initAll);
  document.addEventListener("shopify:section:load", initAll);
})();